import { html, LitElement } from 'lit';
import { updateMicroStepEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import { TagKey } from '@cof/tag-catalog-fs-lienholder-mfe';
import { DebugService } from '@services/debug.service';
import { links } from '@styles/links.style';
import { style } from './lender-info.style';
import { LenderService } from '@services/lender.service';
import { REFI_LIENHOLDER_CONST } from '../../shared/constants/app.constants';
import { pageHeaders } from '@styles/page-headers.style';
import { validateForm } from '../../shared/decorators/validate-form.decorator';
import { preventEvDefault } from '../../shared/decorators/prevent-ev-default.decorator';
import { vibrate } from '../../shared/decorators/vibrate.decorator';
import { SessionService } from '@services/session.service';
import { stratum } from '../../../constants/stratum';
import { AppService } from '../../app.service';
import { customElement, property, state } from 'lit/decorators.js';
import '@components/error-message/error-message.component';
import { HostAppEventsService } from '@services/host-app-events.service.ts';
import { getIsV2Routing } from '@services/routing-v2.service.ts';

@customElement('refi-lender-info')
export class RefiLenderInfo extends LitElement {
  debug: DebugService = new DebugService('RefiLenderInfo');

  constructor() {
    super();

    this.lenderInfo = {
      lienHolderName: LenderService._lienholderModel.lienHolderName ?? '',
      lienHolderAccountNumber:
        LenderService._lienholderModel.lienHolderAccountNumber ?? '',
    };
    this.lenderInfoPrefilled = !!(
      this.lenderInfo.lienHolderAccountNumber && this.lenderInfo.lienHolderName
    );
    this.subHeaderText = this.lenderInfoPrefilled
      ? 'Please confirm your pre-filled lender information below.'
      : 'We need to know your account information first.';

    if (this.lenderInfo.lienHolderName) {
      stratum.publishTag(TagKey.LIENHOLDER_NAME_LENDER_NAME_PREFILLED);
    }

    if (this.lenderInfo.lienHolderAccountNumber) {
      stratum.publishTag(
        TagKey.LIENHOLDER_ACCOUNT_NUMBER_LENDER_ACCOUNT_NUMBER_PREFILLED
      );
    }
  }

  @state()
  apiErrorMessage: string = '';

  @state()
  lenderInfo: any;

  @state()
  loading: boolean = false;

  @state()
  subHeaderText: string;

  @state()
  lenderInfoPrefilled: boolean;

  @property({ type: Boolean })
  showHowToSubmitDocs = false;

  static styles = [pageHeaders, links, style];

  render() {
    this.debug.log('rendered');
    return html`
      <refi-track-pageview pagename="lender"></refi-track-pageview>
      <h2 class="_gb-show">
        Let’s bring your auto loan into the Capital One family.
      </h2>
      <h3 class="_gb-show">
        We’ll pay off your current loan by sending a check to your existing
        lender. ${this.subHeaderText}
      </h3>
      <form
        class="_gb-show"
        name="lenderInfoForm"
        autocomplete="off"
        novalidate
        @submit=${this.submitLenderInfo}
        @keydown="${this.submitForm}"
      >
        <fs-text-field
          type="text"
          value=${this.lenderInfo.lienHolderName}
          @focus=${this.manualFillLienholderName}
          label="Lender Name"
          id="lenderName"
          data-e2e-ref="lienHolderName"
          helper-text="This should be who your auto loan is with now."
          required
          >Lender Name</fs-text-field
        >
        <fs-text-field
          type="text"
          .value=${this.lenderInfo.lienHolderAccountNumber ?? ''}
          @focus=${this.manualFillLienholderAccountNumber}
          label="Lender Account Number"
          id="lenderAccountNumber"
          data-e2e-ref="lienHolderAccountNumber"
          pattern="([0-9a-zA-Z]|-){4,}"
          pattern-mismatch-message="Your lender account number must be at least 4 characters long, and can only contain letters, numbers, and hyphen characters."
          required
          >Lender Account Number</fs-text-field
        >
        <section id="payDetails" data-e2e-ref="lenderInfoKeepPayingText">
          Keep paying your current lender until you see your balance go to
          $0.00.
        </section>
        <fs-button
          id="submitButton"
          variant="primary"
          type="submit"
          class=${this.loading ? 'loading' : ''}
          ?disabled=${this.loading}
        >
          Continue
        </fs-button>
        ${this.apiErrorMessage
          ? html`
              <refi-error-message
                .message="${this.apiErrorMessage}"
              ></refi-error-message>
            `
          : ''}
      </form>
      <refi-common-update-mfe-cache
        mfe-name="wrap-up"
      ></refi-common-update-mfe-cache>
    `;
  }

  submitForm(ev: any) {
    if (ev.key === 'Enter') {
      this.shadowRoot?.querySelector('fs-button')?.click();
    }
  }

  manualFillLienholderName() {
    stratum.publishTag(TagKey.LIENHOLDER_LENDER_NAME_EDITED);
  }

  manualFillLienholderAccountNumber() {
    stratum.publishTag(TagKey.LIENHOLDER_ACCOUNT_NUMBER_EDITED);
  }

  @preventEvDefault()
  @vibrate(50)
  @validateForm()
  async submitLenderInfo(ev: any) {
    this.apiErrorMessage = '';
    this.debug.log('form valid', ev);

    const lienHolderName =
      (this.shadowRoot?.getElementById('lenderName') as HTMLInputElement)
        ?.value || '';
    const lienHolderAccountNumber =
      (
        this.shadowRoot?.getElementById(
          'lenderAccountNumber'
        ) as HTMLInputElement
      )?.value || '';

    this.lenderInfo.lienHolderName = lienHolderName.trim();
    this.lenderInfo.lienHolderAccountNumber = lienHolderAccountNumber.trim();
    console.log(this.lenderInfo);
    this.loading = true;
    const res = await LenderService.persistLienHolder(this.lenderInfo);
    if (res.status === 204) {
      updateMicroStepEvent('Lienholder');
      SessionService.setReferrerId();
      stratum.publishTag(TagKey.LIENHOLDER_SUBMIT_LENDER_INFO);
      AppService.updateState({
        statFlag: 31,
      });
      if (getIsV2Routing()) {
        HostAppEventsService.sendNextMfeEvent();
      } else {
        this.dispatchEvent(
          new CustomEvent('host-app-event', {
            bubbles: true,
            composed: true,
            detail: {
              actionName: 'next-mfe',
              data: {},
            },
          })
        );
      }
    } else {
      this.apiErrorMessage = REFI_LIENHOLDER_CONST.API_ERR_MSG;
    }
    this.loading = false;
  }

  @preventEvDefault()
  toggleShowHowToSubmitDocs(_ev: Event) {
    this.debug.log('toggleShowHowToSubmitDocs called');
    this.showHowToSubmitDocs = !this.showHowToSubmitDocs;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'refi-lender-info': RefiLenderInfo;
  }
}
